/* global axios */
import SipVoiceClient from './sipVoiceClient';
import { useCallsStore } from 'dashboard/stores/calls';
import startVoiceAdminGroups from './voiceAdminGroups';
import startVoiceIntegrationConfig from './voiceIntegrationConfig';

// Đăng ký SIP nền cho INBOUND + badge trạng thái kết nối tổng đài (US2 — T021/T026/T032/T033).
//
// - Chọn voice inbox điều kiện AND: voice_enabled && provider_config.voice_provider
//   === 'asterisk' (root cause #3 của 001: điều kiện OR chọn nhầm inbox).
// - Badge DOM tự quản góc dưới-trái: ready | connecting | error | mic_denied.
//   KHÔNG tạo .vue, KHÔNG patch file core — chỉ element inline tự chứa.
// - Lỗi register KHÔNG nuốt vào console.warn — đẩy vào badge state (chống thất bại âm thầm).
// - Khi Registered: POST /voice/presence mỗi 15s (nguồn lọc đổ chuông FR-003 + agents_ready).
// - Tự phục hồi: đứt kết nối → retry register với backoff, badge tự về ready khi thành công.

const BADGE_ID = 'cw-voice-status-badge';
const GUIDE_ID = 'cw-voice-mic-guide';
const PRESENCE_INTERVAL_MS = 15000;
const WATCHDOG_INTERVAL_MS = 10000;
const FIND_INBOX_RETRY_MS = 10000;
const RETRY_BASE_MS = 5000;
const RETRY_MAX_MS = 60000;

// ===== GĐ2 — Queue ACD: cờ bật chế độ mới (T034) =====
//
// 'acd' (MẶC ĐỊNH GĐ2) = nút "Vào ca / Ra ca" + nhận cuộc qua VoiceDispatchChannel
//        (queue nhiều agent + IVR). Nhân viên KHÔNG cần gõ Console — bật sẵn.
// 'legacy' = giữ auto-register 24/7 cũ (backdoor rollback không cần rebuild):
//        localStorage.setItem('cw_voice_acd_mode', 'legacy')  → về hệ cũ.
//
// Ưu tiên đọc: window.CW_VOICE_ACD_MODE (override runtime) → localStorage → 'acd'.
const DEFAULT_VOICE_MODE = 'acd';
function voiceAcdMode() {
  try {
    if (typeof window !== 'undefined' && window.CW_VOICE_ACD_MODE) {
      return window.CW_VOICE_ACD_MODE;
    }
    const fromStorage =
      typeof localStorage !== 'undefined' &&
      localStorage.getItem('cw_voice_acd_mode');
    return fromStorage || DEFAULT_VOICE_MODE;
  } catch (e) {
    return DEFAULT_VOICE_MODE;
  }
}

function isAcdMode() {
  return voiceAcdMode() === 'acd';
}

const STATE_META = {
  ready: { color: '#22c55e', label: 'Sẵn sàng nhận cuộc gọi' },
  connecting: { color: '#eab308', label: 'Đang kết nối tổng đài...' },
  error: { color: '#ef4444', label: 'Mất kết nối tổng đài' },
  mic_denied: { color: '#ef4444', label: 'Chưa cấp quyền microphone' },
};

let started = false;
let storeRef = null;
let voiceInboxId = null;
let currentState = null;
let micDenied = false;
let retryTimer = null;
let retryDelayMs = RETRY_BASE_MS;
let presenceTimer = null;
let watchdogTimer = null;

// ===== Badge DOM =====

function ensureBadge() {
  let el = document.getElementById(BADGE_ID);
  if (!el) {
    el = document.createElement('div');
    el.id = BADGE_ID;
    el.style.cssText =
      'position:fixed;left:12px;bottom:12px;z-index:2147483000;' +
      'display:flex;align-items:center;gap:6px;padding:4px 10px;' +
      'border-radius:9999px;background:rgba(17,24,39,.92);color:#fff;' +
      'font:12px/1.4 system-ui,sans-serif;box-shadow:0 2px 8px rgba(0,0,0,.35);' +
      'cursor:default;user-select:none;';
    const dot = document.createElement('span');
    dot.style.cssText =
      'width:8px;height:8px;border-radius:50%;background:#9ca3af;flex:none;';
    const text = document.createElement('span');
    el.appendChild(dot);
    el.appendChild(text);
    el.addEventListener('click', () => {
      if (currentState === 'mic_denied') showMicDeniedGuide();
    });
    document.body.appendChild(el);
  }
  return el;
}

// Cập nhật badge. state: 'ready' | 'connecting' | 'error' | 'mic_denied'.
// mic_denied có ưu tiên: đang denied thì 'ready' vẫn hiển thị mic_denied
// (đăng ký OK nhưng không thể đàm thoại → agent phải biết ngay).
export function setVoiceUiState(state, reason = '') {
  const effective = state === 'ready' && micDenied ? 'mic_denied' : state;
  currentState = effective;
  const meta = STATE_META[effective] || STATE_META.error;
  const el = ensureBadge();
  el.children[0].style.background = meta.color;
  el.children[1].textContent = meta.label;
  el.title = reason || meta.label;
}

// Toast hướng dẫn cấp lại quyền microphone (T033) — element tự chứa, tiếng Việt.
export function showMicDeniedGuide() {
  const existing = document.getElementById(GUIDE_ID);
  if (existing) existing.remove();

  const el = document.createElement('div');
  el.id = GUIDE_ID;
  el.style.cssText =
    'position:fixed;left:12px;bottom:52px;z-index:2147483001;max-width:320px;' +
    'padding:12px 14px;border-radius:10px;background:#111827;color:#fff;' +
    'font:13px/1.5 system-ui,sans-serif;box-shadow:0 4px 16px rgba(0,0,0,.4);';

  const title = document.createElement('div');
  title.style.cssText = 'font-weight:600;margin-bottom:6px;';
  title.textContent = 'Chưa cấp quyền microphone';

  const body = document.createElement('div');
  body.textContent =
    'Trình duyệt đang chặn microphone nên không thể đàm thoại. ' +
    'Bấm icon 🔒 trên thanh địa chỉ → chọn Cho phép (Allow) cho Microphone → tải lại trang.';

  const close = document.createElement('button');
  close.textContent = 'Đóng';
  close.style.cssText =
    'margin-top:8px;padding:2px 10px;border:1px solid #4b5563;border-radius:6px;' +
    'background:transparent;color:#fff;cursor:pointer;font:12px system-ui,sans-serif;';
  close.addEventListener('click', () => el.remove());

  el.appendChild(title);
  el.appendChild(body);
  el.appendChild(close);
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 20000);
}

// ===== Hooks cho sipVoiceClient (Registerer state → badge + tự phục hồi) =====

export function notifyVoiceRegistrationUp() {
  retryDelayMs = RETRY_BASE_MS; // thành công → reset backoff
  setVoiceUiState('ready');
  startPresencePing();
}

export function notifyVoiceRegistrationDown(reason) {
  stopPresencePing();
  setVoiceUiState('error', reason);
  scheduleReRegister();
}

export function setMicDenied(denied) {
  micDenied = !!denied;
  if (micDenied) {
    setVoiceUiState('mic_denied');
  } else if (SipVoiceClient.isRegistered) {
    setVoiceUiState('ready');
  } else if (currentState === 'mic_denied') {
    setVoiceUiState('connecting');
  }
}

// ===== Interval chống throttle tab nền =====
//
// Chrome "intensive throttling" ép setInterval của TAB NỀN còn 1 nhịp/phút →
// heartbeat 15s giãn ~60s → presence hết TTL → agent rơi offline âm thầm dù
// badge vẫn "Sẵn sàng" (root cause mọi cuộc gọi rơi voicemail 22-25/07).
// Dedicated Worker KHÔNG bị throttle theo visibility → tick từ Worker, gọi fn
// ở main thread. Fallback setInterval nếu môi trường chặn Worker/Blob.
// Trả về hàm stop() — thay thế cặp setInterval/clearInterval.
function startBackgroundSafeInterval(fn, ms) {
  try {
    const src = `setInterval(function () { postMessage(1); }, ${ms});`;
    const url = URL.createObjectURL(
      new Blob([src], { type: 'application/javascript' })
    );
    const worker = new Worker(url);
    URL.revokeObjectURL(url);
    worker.onmessage = () => fn();
    return () => worker.terminate();
  } catch (e) {
    const timer = setInterval(fn, ms);
    return () => clearInterval(timer);
  }
}

// ===== Presence ping (T026a): server chỉ đổ chuông tới agent có presence tươi =====

function accountIdFromRoute() {
  const path = window.location.pathname;
  return path.includes('/app/accounts') ? path.split('/')[3] : '';
}

function sendPresence() {
  const accountId = accountIdFromRoute();
  if (!accountId) return;
  axios
    .post(`/api/v1/accounts/${accountId}/voice/presence`)
    .then(() => {
      // Presence OK + còn đăng ký → xác nhận ready (tự sửa nếu badge đang error).
      if (SipVoiceClient.isRegistered) setVoiceUiState('ready');
    })
    .catch(e => {
      // Presence rớt = server không biết agent trực → sẽ KHÔNG đổ chuông tới
      // agent này. Đây là lỗi thật, phải hiện ra badge (không nuốt).
      setVoiceUiState('error', `Không gửi được presence: ${e?.message || e}`);
    });
}

function startPresencePing() {
  if (presenceTimer) return;
  sendPresence();
  presenceTimer = startBackgroundSafeInterval(
    sendPresence,
    PRESENCE_INTERVAL_MS
  );
}

function stopPresencePing() {
  if (presenceTimer) presenceTimer(); // stop function từ startBackgroundSafeInterval
  presenceTimer = null;
}

// ===== Đăng ký + tự phục hồi =====

async function doRegister() {
  if (!voiceInboxId) return;
  // Đang đàm thoại → không teardown/dựng lại UA giữa cuộc; thử lại sau.
  if (SipVoiceClient.hasActiveConnection && !SipVoiceClient.isRegistered) {
    scheduleReRegister();
    return;
  }
  setVoiceUiState('connecting');
  try {
    await SipVoiceClient.registerBackground(voiceInboxId);
    // Kết quả thật (REGISTER 200/lỗi) về qua Registerer stateChange →
    // notifyVoiceRegistrationUp/Down. Ở đây chỉ bắt lỗi dựng UA/token.
  } catch (e) {
    setVoiceUiState('error', e?.message || 'Đăng ký SIP thất bại');
    scheduleReRegister();
  }
}

function scheduleReRegister() {
  if (!voiceInboxId || retryTimer) return;
  const delay = retryDelayMs;
  retryDelayMs = Math.min(retryDelayMs * 2, RETRY_MAX_MS);
  retryTimer = setTimeout(() => {
    retryTimer = null;
    doRegister();
  }, delay);
}

// Watchdog 10s: bảo đảm badge phản ánh đứt kết nối ≤30s (SC-005) kể cả khi
// SIP.js không phát event (WSS treo nửa chừng), và làm lưới an toàn cho retry.
function startWatchdog() {
  if (watchdogTimer) return;
  watchdogTimer = setInterval(() => {
    if (!voiceInboxId || SipVoiceClient.isRegistered) return;
    if (currentState === 'ready') {
      notifyVoiceRegistrationDown('Mất đăng ký SIP với tổng đài');
    } else if (!retryTimer && currentState !== 'connecting') {
      scheduleReRegister();
    }
  }, WATCHDOG_INTERVAL_MS);
}

// ===== Probe quyền micro NGAY khi vào trực (không đợi có cuộc gọi) =====

async function probeMicPermission() {
  try {
    if (!navigator.permissions?.query) return;
    const status = await navigator.permissions.query({ name: 'microphone' });
    const apply = () => setMicDenied(status.state === 'denied');
    apply();
    // Agent đổi quyền trong site settings → badge cập nhật không cần reload.
    status.onchange = apply;
  } catch (e) {
    // Trình duyệt không hỗ trợ query('microphone') — denial sẽ được phát hiện
    // lúc accept cuộc gọi (sipVoiceClient.ensureMicrophoneAccess).
  }
}

// ===== Entry point =====

// Voice inbox asterisk: AND bắt buộc (không OR). Store getter trả payload API
// snake_case; chấp nhận thêm camelCase như helper inbox.js của Chatwoot.
function findAsteriskVoiceInbox(store) {
  const inboxes = store?.getters?.['inboxes/getInboxes'] || [];
  return inboxes.find(i => {
    const voiceEnabled = i?.voice_enabled ?? i?.voiceEnabled;
    const providerConfig = i?.provider_config ?? i?.providerConfig;
    const voiceProvider =
      providerConfig?.voice_provider ?? providerConfig?.voiceProvider;
    return !!voiceEnabled && voiceProvider === 'asterisk';
  });
}

function tryStart() {
  let inbox = null;
  try {
    inbox = findAsteriskVoiceInbox(storeRef);
  } catch (e) {
    inbox = null;
  }
  if (!inbox?.id) {
    // Inbox chưa hydrate lúc mount hoặc account chưa có voice inbox asterisk —
    // KHÔNG hiện badge lỗi (agent không thuộc inbox voice là bình thường), thử lại.
    setTimeout(tryStart, FIND_INBOX_RETRY_MS);
    return;
  }
  voiceInboxId = inbox.id;
  ensureDialpad();
  probeMicPermission();
  startWatchdog();
  doRegister();
}

// ===== Dialpad gọi ra số bất kỳ (US3 mở rộng) =====
// Nút nổi luôn hiển thị → nhập số → tìm/tạo contact theo số → gọi qua ĐÚNG luồng
// native (contacts/initiateCall + callsStore.addCall) → FloatingCallWidget tự
// auto-join → SIP.js INVITE. Không đụng file core, không cần contact có sẵn.

const DIALPAD_ID = 'cw-voice-dialpad';

// Chuẩn hoá số nhập về E.164 VN (đồng bộ Adapter.normalize_target phía backend).
function toE164(raw) {
  const digits = String(raw).replace(/\D/g, '');
  if (!digits) return null;
  if (digits.startsWith('84')) return `+${digits}`;
  if (digits.startsWith('0')) return `+84${digits.slice(1)}`;
  return `+84${digits}`;
}

async function findOrCreateContact(accountId, phoneE164) {
  const base = `/api/v1/accounts/${accountId}/contacts`;
  // 1. Tìm contact theo số (đã lưu → tái dùng, không tạo trùng).
  try {
    const { data } = await axios.get(`${base}/search`, {
      params: { q: phoneE164 },
    });
    const match = (data?.payload || []).find(
      c => (c.phone_number || '').replace(/\s/g, '') === phoneE164
    );
    if (match?.id) return match.id;
  } catch (e) {
    /* search lỗi → thử tạo mới */
  }
  // 2. Chưa có → tạo contact mới với số này.
  const { data } = await axios.post(base, {
    name: phoneE164,
    phone_number: phoneE164,
  });
  return data?.payload?.contact?.id || data?.payload?.id;
}

async function placeOutboundCall(rawNumber) {
  const accountId = accountIdFromRoute();
  const phone = toE164(rawNumber);
  if (!accountId || !phone || !voiceInboxId) {
    return { ok: false, msg: 'Chưa sẵn sàng (chưa có inbox voice hoặc số không hợp lệ)' };
  }
  try {
    const contactId = await findOrCreateContact(accountId, phone);
    if (!contactId) return { ok: false, msg: 'Không tạo được contact' };

    // ĐÚNG luồng native: dispatch initiateCall → addCall outbound → widget tự join.
    const response = await storeRef.dispatch('contacts/initiateCall', {
      contactId,
      inboxId: voiceInboxId,
    });
    const callSid = response?.call_sid;
    const conversationId = response?.conversation_id;
    if (!callSid) return { ok: false, msg: 'Tổng đài không khởi tạo được cuộc gọi' };

    useCallsStore().addCall({
      callSid,
      conversationId,
      inboxId: voiceInboxId,
      callDirection: 'outbound',
    });
    return { ok: true };
  } catch (e) {
    const msg = e?.response?.data?.error || e?.message || 'Gọi thất bại';
    return { ok: false, msg };
  }
}

// ===== Giao diện dialpad — native theo design system Chatwoot =====
// Chỉ phần HIỂN THỊ. Dùng CSS variables của Chatwoot (RGB triplet cascade từ
// :root / .dark trên <html>) → tự đổi sáng/tối; kèm fallback màu sáng cho an
// toàn. Primary = --n-brand (#2781F6). CSS gom trong 1 <style> tự quản, class
// prefix cw-voice- để không đụng CSS Chatwoot.

const DIALPAD_STYLE_ID = 'cw-voice-dialpad-styles';
const ICON_PHONE =
  '<svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true">' +
  '<path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.02-.24 11.36 11.36 0 003.56.57 1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.24.2 2.45.57 3.56a1 1 0 01-.25 1.03l-2.2 2.2z"/></svg>';
const ICON_CLOSE =
  '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">' +
  '<path d="M6 6l12 12M18 6L6 18"/></svg>';

const DIALPAD_CSS = `
#${DIALPAD_ID}{position:fixed;left:12px;bottom:44px;z-index:2147483000;
  display:flex;flex-direction:column;align-items:flex-start;
  font-family:'Inter',-apple-system,system-ui,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;}
#${DIALPAD_ID} *{box-sizing:border-box;}
.cw-voice-toggle{display:inline-flex;align-items:center;gap:7px;
  padding:8px 14px 8px 12px;border:none;border-radius:9999px;cursor:pointer;
  background:#2781F6;color:#fff;font-size:13px;font-weight:600;line-height:1;
  box-shadow:0 4px 14px rgba(39,129,246,.4);transition:filter .15s,box-shadow .15s;}
.cw-voice-toggle:hover{filter:brightness(1.08);}
.cw-voice-toggle:focus-visible{outline:2px solid #2781F6;outline-offset:2px;}
.cw-voice-toggle svg{display:block;flex:none;}
.cw-voice-panel{width:260px;margin-bottom:10px;padding:14px;
  border-radius:14px;border:1px solid rgb(var(--border-weak,234 234 234));
  background:rgb(var(--card-color,255 255 255));
  box-shadow:0 12px 34px rgba(0,0,0,.16),0 2px 8px rgba(0,0,0,.08);}
.cw-voice-panel[hidden]{display:none;}
.cw-voice-head{display:flex;align-items:center;justify-content:space-between;margin-bottom:10px;}
.cw-voice-title{font-size:13px;font-weight:600;color:rgb(var(--slate-12,28 32 36));}
.cw-voice-close{display:inline-flex;align-items:center;justify-content:center;
  width:24px;height:24px;padding:0;border:none;border-radius:6px;cursor:pointer;
  background:transparent;color:rgb(var(--slate-11,96 100 108));transition:background .15s;}
.cw-voice-close:hover{background:rgba(147,153,176,.18);}
.cw-voice-close svg{display:block;}
.cw-voice-input{width:100%;height:38px;padding:0 12px;border-radius:9px;
  border:1px solid rgb(var(--border-strong,226 227 231));
  background:rgb(var(--surface-1,255 255 255));
  color:rgb(var(--slate-12,28 32 36));font-size:14px;line-height:36px;
  outline:none;transition:border-color .15s,box-shadow .15s;}
.cw-voice-input::placeholder{color:rgb(var(--slate-10,128 131 141));}
.cw-voice-input:focus{border-color:#2781F6;box-shadow:0 0 0 3px rgba(39,129,246,.22);}
.cw-voice-call{width:100%;height:38px;margin-top:10px;border:none;border-radius:9px;
  cursor:pointer;background:#2781F6;color:#fff;font-size:14px;font-weight:600;
  display:inline-flex;align-items:center;justify-content:center;gap:6px;transition:filter .15s;}
.cw-voice-call:hover:not(:disabled){filter:brightness(1.08);}
.cw-voice-call:disabled{opacity:.6;cursor:default;}
.cw-voice-call svg{display:block;flex:none;}
.cw-voice-hint{min-height:16px;margin-top:8px;font-size:12px;line-height:1.4;
  color:rgb(var(--slate-11,96 100 108));}
.cw-voice-hint--info{color:#2781F6;}
.cw-voice-hint--ok{color:rgb(var(--teal-11,0 133 115));}
.cw-voice-hint--error{color:rgb(var(--ruby-11,202 36 77));}
`;

function ensureDialpadStyles() {
  if (document.getElementById(DIALPAD_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = DIALPAD_STYLE_ID;
  style.textContent = DIALPAD_CSS;
  document.head.appendChild(style);
}

function ensureDialpad() {
  if (document.getElementById(DIALPAD_ID)) return;
  ensureDialpadStyles();

  const wrap = document.createElement('div');
  wrap.id = DIALPAD_ID;

  // Panel đặt TRƯỚC toggle trong DOM → popover mở LÊN TRÊN, neo góc dưới-trái.
  const panel = document.createElement('div');
  panel.className = 'cw-voice-panel';
  panel.hidden = true;
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-label', 'Gọi ra số điện thoại');

  const head = document.createElement('div');
  head.className = 'cw-voice-head';
  const title = document.createElement('span');
  title.className = 'cw-voice-title';
  title.textContent = 'Gọi ra';
  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'cw-voice-close';
  close.title = 'Đóng';
  close.setAttribute('aria-label', 'Đóng');
  close.innerHTML = ICON_CLOSE;
  head.append(title, close);

  const input = document.createElement('input');
  input.type = 'tel';
  input.className = 'cw-voice-input';
  input.placeholder = 'VD: 0912 345 678';
  input.autocomplete = 'off';
  input.setAttribute('aria-label', 'Số điện thoại');

  const callBtn = document.createElement('button');
  callBtn.type = 'button';
  callBtn.className = 'cw-voice-call';
  callBtn.title = 'Gọi';
  callBtn.innerHTML = `${ICON_PHONE}<span>Gọi</span>`;

  const hint = document.createElement('div');
  hint.className = 'cw-voice-hint';
  hint.setAttribute('aria-live', 'polite');

  panel.append(head, input, callBtn, hint);

  const toggle = document.createElement('button');
  toggle.type = 'button';
  toggle.className = 'cw-voice-toggle';
  toggle.title = 'Gọi ra số bất kỳ';
  toggle.setAttribute('aria-expanded', 'false');
  toggle.innerHTML = `${ICON_PHONE}<span>Gọi ra</span>`;

  const setHint = (text, kind) => {
    hint.textContent = text || '';
    hint.className = 'cw-voice-hint' + (kind ? ` cw-voice-hint--${kind}` : '');
  };
  const openPanel = () => {
    panel.hidden = false;
    toggle.setAttribute('aria-expanded', 'true');
    input.focus();
  };
  const closePanel = () => {
    panel.hidden = true;
    toggle.setAttribute('aria-expanded', 'false');
  };

  toggle.addEventListener('click', () => {
    if (panel.hidden) openPanel();
    else closePanel();
  });
  close.addEventListener('click', closePanel);

  const doCall = async () => {
    const num = input.value.trim();
    if (!num) return;
    setHint('Đang gọi...', 'info');
    callBtn.disabled = true;
    const res = await placeOutboundCall(num);
    callBtn.disabled = false;
    if (res.ok) {
      setHint('Đã khởi tạo cuộc gọi', 'ok');
      input.value = '';
      setTimeout(() => {
        closePanel();
        setHint('');
      }, 1500);
    } else {
      setHint(res.msg, 'error');
    }
  };
  callBtn.addEventListener('click', doCall);
  input.addEventListener('keydown', e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      doCall();
    }
  });
  // Esc để đóng popover (a11y) — bắt ở wrapper nên hiệu lực khi con đang focus.
  wrap.addEventListener('keydown', e => {
    if (e.key === 'Escape' && !panel.hidden) {
      closePanel();
      toggle.focus();
    }
  });

  wrap.append(panel, toggle);
  document.body.appendChild(wrap);
}

// ================================================================
// GĐ2 — Queue ACD (T034): nút Vào ca/Ra ca + nhận cuộc offer qua dispatch.
// Toàn bộ state/DOM dưới đây tách biệt hoàn toàn khỏi badge/dialpad legacy ở
// trên — không dùng chung biến `voiceInboxId`/`storeRef` (ACD không đăng ký
// theo inbox mà theo shift/skills). Chỉ khởi tạo khi isAcdMode() === true.
// ================================================================

const SHIFT_BTN_ID = 'cw-voice-acd-shift';
const OFFER_POPUP_ID = 'cw-voice-acd-offer';
const SHIFT_STORAGE_KEY = 'cw_voice_acd_shift'; // { skills: string[] } | null
const ACD_PRESENCE_INTERVAL_MS = 15000;
const ACD_POLL_INTERVAL_MS = 3000; // fallback khi không dựng được ActionCable

let acdStoreRef = null;
let acdOnShift = false;
let acdSkills = [];
let acdPresenceTimer = null;
let acdCableSub = null; // subscription ActionCable (null nếu đang dùng polling)
let acdPollTimer = null;
let acdOfferedCallSid = null; // call_sid đang hiện popup (chống 2 popup chồng)

// ===== Helpers dùng chung với legacy (accountIdFromRoute đã có ở trên) =====

function acdCurrentUser() {
  try {
    return (
      acdStoreRef?.getters?.getCurrentUser ||
      acdStoreRef?.getters?.['auth/getCurrentUser'] ||
      null
    );
  } catch (e) {
    return null;
  }
}

// ===== Persist trạng thái ca qua reload (T034.a) =====

function saveShiftState(skills) {
  try {
    localStorage.setItem(SHIFT_STORAGE_KEY, JSON.stringify({ skills }));
  } catch (e) {
    /* localStorage bị chặn — mất trạng thái sau reload, không chặn luồng chính */
  }
}

function clearShiftState() {
  try {
    localStorage.removeItem(SHIFT_STORAGE_KEY);
  } catch (e) {
    /* bỏ qua */
  }
}

function loadShiftState() {
  try {
    const raw = localStorage.getItem(SHIFT_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

// ===== Nút nổi Vào ca / Ra ca =====

function ensureShiftButton() {
  let el = document.getElementById(SHIFT_BTN_ID);
  if (el) return el;
  ensureDialpadStyles(); // tái dùng biến CSS Chatwoot đã khai báo (rgb(var(--...)))
  el = document.createElement('button');
  el.id = SHIFT_BTN_ID;
  el.type = 'button';
  el.style.cssText =
    'position:fixed;right:12px;bottom:12px;z-index:2147483000;' +
    'display:inline-flex;align-items:center;gap:7px;padding:8px 16px;' +
    'border:none;border-radius:9999px;cursor:pointer;font-size:13px;' +
    'font-weight:600;line-height:1;box-shadow:0 4px 14px rgba(0,0,0,.3);' +
    'transition:filter .15s;';
  el.addEventListener('click', onShiftButtonClick);
  document.body.appendChild(el);
  return el;
}

function renderShiftButton() {
  const el = ensureShiftButton();
  if (acdOnShift) {
    el.textContent = 'Đang trong ca — Ra ca';
    el.style.background = '#ef4444';
    el.style.color = '#fff';
  } else {
    el.textContent = 'Vào ca';
    el.style.background = '#22c55e';
    el.style.color = '#fff';
  }
}

async function onShiftButtonClick() {
  if (acdOnShift) {
    await stopShift();
  } else {
    await startShift(acdSkills);
  }
}

// ===== Shift start/stop (T034.b, T034.c) =====

let shiftStarting = false;
async function startShift(skills) {
  const accountId = accountIdFromRoute();
  if (!accountId) return;
  // Guard chống gọi TRÙNG (bug code 1006): auto-restore ca từ localStorage (mount) +
  // agent bấm nút → 2 lần startShift song song → registerWithCredentials lần 2 chạy
  // destroyDevice() hủy UA lần 1 ĐANG connecting → WSS đóng giữa chừng (code 1006).
  // Đang start dở HOẶC đã register sống → bỏ qua lần gọi thừa.
  if (shiftStarting || acdOnShift || SipVoiceClient.isRegistered) return;
  shiftStarting = true;
  setVoiceUiState('connecting');
  try {
    const { data } = await axios.post(
      `/api/v1/accounts/${accountId}/voice/agent/shift/start`,
      // skills rỗng → server tự lấy toàn bộ nhóm cấu hình của account (per-account,
      // không hardcode kd/hotro ở client).
      { skills: Array.isArray(skills) ? skills : [], client: 'browser' }
    );
    const sip = data?.sip_credentials;
    if (!sip) throw new Error('Thiếu sip_credentials từ server');

    // Register MEDIA 1 lần cho cả ca (KHÁC token theo inbox của legacy).
    // Contract shift_start hiện KHÔNG trả ice_servers (chỉ endpoint/sip_credentials/
    // shift_id) → data?.ice_servers luôn undefined, buildUserAgentFromCredentials
    // tự fallback mảng rỗng (an toàn, không throw). Nếu backend bổ sung ice_servers
    // sau này thì tự nhặt được, không cần sửa dòng này.
    await SipVoiceClient.registerWithCredentials(sip, data?.ice_servers);

    acdOnShift = true;
    acdSkills = Array.isArray(skills) ? skills : [];
    saveShiftState(acdSkills);
    renderShiftButton();
    startAcdPresencePing();
    subscribeVoiceDispatch();
  } catch (e) {
    const msg =
      e?.response?.data?.error || e?.message || 'Vào ca thất bại';
    setVoiceUiState('error', msg);
  } finally {
    shiftStarting = false;
  }
}

async function stopShift() {
  const accountId = accountIdFromRoute();
  try {
    if (accountId) {
      await axios.post(`/api/v1/accounts/${accountId}/voice/agent/shift/stop`);
    }
  } catch (e) {
    // Best-effort: dù API lỗi vẫn phải dọn state client để agent không kẹt "đang ca".
    setVoiceUiState('error', `Ra ca lỗi phía server: ${e?.message || e}`);
  }
  SipVoiceClient.destroyDevice();
  stopAcdPresencePing();
  unsubscribeVoiceDispatch();
  removeOfferPopup();
  acdOnShift = false;
  clearShiftState();
  renderShiftButton();
}

// ===== Presence ping khi trong ca (T034 mục 4: không gửi endpoint) =====

function sendAcdPresence() {
  const accountId = accountIdFromRoute();
  if (!accountId) return;
  axios
    .post(`/api/v1/accounts/${accountId}/voice/agent/presence`)
    .catch(e => {
      setVoiceUiState('error', `Không gửi được presence ca: ${e?.message || e}`);
    });
}

// Beat tức thời khi tab hiện lại / mạng có lại — bù nhịp bị trễ lúc tab nền.
function onAcdVisibilityBeat() {
  if (!acdOnShift) return;
  if (document.visibilityState === 'visible') sendAcdPresence();
}

function onAcdOnlineBeat() {
  if (acdOnShift) sendAcdPresence();
}

function startAcdPresencePing() {
  if (acdPresenceTimer) return;
  sendAcdPresence();
  acdPresenceTimer = startBackgroundSafeInterval(
    sendAcdPresence,
    ACD_PRESENCE_INTERVAL_MS
  );
  document.addEventListener('visibilitychange', onAcdVisibilityBeat);
  window.addEventListener('online', onAcdOnlineBeat);
}

function stopAcdPresencePing() {
  if (acdPresenceTimer) acdPresenceTimer(); // stop function từ startBackgroundSafeInterval
  acdPresenceTimer = null;
  document.removeEventListener('visibilitychange', onAcdVisibilityBeat);
  window.removeEventListener('online', onAcdOnlineBeat);
}

// ===== Popup "Cuộc gọi đến" (T034.d) =====

function ensureOfferPopupStyles() {
  const STYLE_ID = 'cw-voice-acd-offer-styles';
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
#${OFFER_POPUP_ID}{position:fixed;right:12px;bottom:60px;z-index:2147483002;
  width:280px;padding:14px;border-radius:14px;
  border:1px solid rgb(var(--border-weak,234 234 234));
  background:rgb(var(--card-color,255 255 255));
  box-shadow:0 12px 34px rgba(0,0,0,.2),0 2px 8px rgba(0,0,0,.08);
  font-family:'Inter',-apple-system,system-ui,'Segoe UI',Roboto,sans-serif;}
.cw-voice-acd-offer-title{font-size:13px;font-weight:700;margin-bottom:4px;
  color:rgb(var(--slate-12,28 32 36));}
.cw-voice-acd-offer-sub{font-size:12px;margin-bottom:10px;
  color:rgb(var(--slate-11,96 100 108));}
.cw-voice-acd-offer-actions{display:flex;gap:8px;}
.cw-voice-acd-offer-actions button{flex:1;height:36px;border:none;
  border-radius:8px;cursor:pointer;font-size:13px;font-weight:600;}
.cw-voice-acd-accept{background:#22c55e;color:#fff;}
.cw-voice-acd-reject{background:rgba(147,153,176,.18);
  color:rgb(var(--slate-12,28 32 36));}
`;
  document.head.appendChild(style);
}

// Hiện pop-up "Cuộc gọi đến từ {from_number}". callSid dùng để accept/reject
// đúng cuộc; nếu đã có popup cho callSid khác thì thay thế (mỗi lần offer 1 cuộc).
function showOfferPopup({ call_sid: callSid, from_number: fromNumber }) {
  removeOfferPopup();
  ensureOfferPopupStyles();
  acdOfferedCallSid = callSid;

  const el = document.createElement('div');
  el.id = OFFER_POPUP_ID;
  el.setAttribute('role', 'alertdialog');
  el.setAttribute('aria-label', 'Cuộc gọi đến');

  const title = document.createElement('div');
  title.className = 'cw-voice-acd-offer-title';
  title.textContent = `Cuộc gọi đến từ ${fromNumber || 'số ẩn danh'}`;

  const sub = document.createElement('div');
  sub.className = 'cw-voice-acd-offer-sub';
  sub.textContent = 'Bấm Nhận để trả lời hoặc Từ chối để chuyển agent khác.';

  const actions = document.createElement('div');
  actions.className = 'cw-voice-acd-offer-actions';

  const acceptBtn = document.createElement('button');
  acceptBtn.type = 'button';
  acceptBtn.className = 'cw-voice-acd-accept';
  acceptBtn.textContent = 'Nhận';
  acceptBtn.addEventListener('click', () => acceptOfferedCall(callSid));

  const rejectBtn = document.createElement('button');
  rejectBtn.type = 'button';
  rejectBtn.className = 'cw-voice-acd-reject';
  rejectBtn.textContent = 'Từ chối';
  rejectBtn.addEventListener('click', () => rejectOfferedCall(callSid));

  actions.append(acceptBtn, rejectBtn);
  el.append(title, sub, actions);
  document.body.appendChild(el);
}

function removeOfferPopup(callSid) {
  // Nếu truyền callSid mà không khớp popup đang hiện → không phải cuộc này, bỏ qua
  // (tránh event taken/revoked của cuộc CŨ xoá nhầm popup cuộc MỚI đang hiện).
  if (callSid && acdOfferedCallSid && callSid !== acdOfferedCallSid) return;
  const el = document.getElementById(OFFER_POPUP_ID);
  if (el) el.remove();
  acdOfferedCallSid = null;
}

async function acceptOfferedCall(callSid) {
  const accountId = accountIdFromRoute();
  if (!accountId) return;
  try {
    const { data } = await axios.post(
      `/api/v1/accounts/${accountId}/voice/agent/calls/${callSid}/accept`
    );
    // Guard U1 có sẵn (sipVoiceClient.armAutoAccept) — vũ trang bridge_id để
    // INVITE media do ARI originate tới được auto-accept đúng leg của agent này.
    if (data?.bridge_id) SipVoiceClient.armAutoAccept(data.bridge_id);
    removeOfferPopup(callSid);
  } catch (e) {
    setVoiceUiState('error', `Nhận cuộc gọi thất bại: ${e?.message || e}`);
    removeOfferPopup(callSid);
  }
}

async function rejectOfferedCall(callSid) {
  const accountId = accountIdFromRoute();
  removeOfferPopup(callSid);
  if (!accountId) return;
  try {
    await axios.post(
      `/api/v1/accounts/${accountId}/voice/agent/calls/${callSid}/reject`
    );
  } catch (e) {
    // Từ chối lỗi phía server không chặn UI — popup đã gỡ, agent đã "buông" cuộc này.
    setVoiceUiState('error', `Từ chối cuộc gọi lỗi: ${e?.message || e}`);
  }
}

// ===== Nhận event dispatch: ActionCable thật (ưu tiên) → polling (fallback) =====
//
// Chatwoot core expose window.WS_URL (route cable) trong layout dashboard.
// Consumer dùng @rails/actioncable (dependency có sẵn trong bundle Chatwoot vì
// core cũng dùng nó cho realtime conversation). Auth theo pubsub_token + user_id
// của CHÍNH user hiện tại (giống RoomChannel) — subscribe params khớp
// VoiceDispatchChannel#subscribed phía backend (overlay/app/channels).
//
// GHI CHÚ QUAN TRỌNG: nếu import '@rails/actioncable' lỗi hoặc consumer không
// dựng được (ví dụ bundle Chatwoot đổi cách expose WS_URL/pubsub_token ở version
// khác), code TỰ ĐỘNG rơi về polling GET /voice/agent/queue + /voice/agent/state
// mỗi 3s — ĐÂY LÀ FALLBACK TẠM, cần thay bằng ActionCable thật khi xác nhận lại
// cách Chatwoot phiên bản đang chạy expose consumer (kiểm tra
// dashboard/helper/actionCable.js hoặc app.config.globalProperties trong core).
async function subscribeVoiceDispatch() {
  const ok = await trySubscribeActionCable();
  if (!ok) startDispatchPolling();
}

async function trySubscribeActionCable() {
  try {
    const user = acdCurrentUser();
    const accountId = accountIdFromRoute();
    const pubsubToken = user?.pubsub_token;
    const userId = user?.id;
    if (!pubsubToken || !userId || !accountId) return false;

    // Import động: nếu package không có trong bundle, catch bên dưới xử lý fallback.
    const { createConsumer } = await import('@rails/actioncable');
    const wsUrl =
      (typeof window !== 'undefined' && window.WS_URL) || '/cable';
    const consumer = createConsumer(wsUrl);

    acdCableSub = consumer.subscriptions.create(
      {
        channel: 'VoiceDispatchChannel',
        account_id: accountId,
        user_id: userId,
        pubsub_token: pubsubToken,
      },
      {
        received: handleDispatchEvent,
      }
    );
    // Có subscription thật → không cần polling song song.
    stopDispatchPolling();
    return true;
  } catch (e) {
    // Không dựng được ActionCable (thiếu package/consumer) → để subscribeVoiceDispatch
    // rơi về polling, không throw để không chặn luồng vào ca.
    return false;
  }
}

function unsubscribeVoiceDispatch() {
  try {
    acdCableSub?.unsubscribe?.();
  } catch (e) {
    /* bỏ qua lỗi unsubscribe khi teardown */
  }
  acdCableSub = null;
  stopDispatchPolling();
}

// Xử lý message ActionCable đúng theo contract phase-06 (voice.call.offered/taken/
// revoked/queue.updated/agent.state). Không xử lý queue.updated/agent.state ở
// UI này (chưa có yêu cầu hiển thị) — chỉ log-free no-op để không nuốt lỗi giả.
function handleDispatchEvent(payload) {
  if (!payload || typeof payload !== 'object') return;
  const { event, data } = payload;
  switch (event) {
    case 'voice.call.offered':
      showOfferPopup(data || {});
      break;
    case 'voice.call.taken':
    case 'voice.call.revoked':
      removeOfferPopup(data?.call_sid);
      break;
    default:
      break; // voice.queue.updated / voice.agent.state — chưa cần xử lý ở đây
  }
}

// ===== Fallback polling (mục 3): thay thế tạm cho ActionCable =====

let acdLastQueueSids = new Set();

async function pollDispatchState() {
  const accountId = accountIdFromRoute();
  if (!accountId) return;
  try {
    const { data } = await axios.get(
      `/api/v1/accounts/${accountId}/voice/agent/queue`
    );
    const waiting = Array.isArray(data?.waiting) ? data.waiting : [];
    const currentSids = new Set(waiting.map(w => w.call_sid));

    // Cuộc mới xuất hiện trong queue mà popup chưa hiện → coi như "offered".
    // Polling KHÔNG có from_number/skill (API /queue không trả) — hiển thị
    // placeholder, agent vẫn accept/reject đúng qua call_sid.
    waiting.forEach(w => {
      if (!acdLastQueueSids.has(w.call_sid) && w.call_sid !== acdOfferedCallSid) {
        showOfferPopup({ call_sid: w.call_sid, from_number: null });
      }
    });

    // Cuộc đang hiện popup mà biến mất khỏi queue (agent khác nhận / huỷ) → gỡ.
    if (acdOfferedCallSid && !currentSids.has(acdOfferedCallSid)) {
      removeOfferPopup(acdOfferedCallSid);
    }
    acdLastQueueSids = currentSids;
  } catch (e) {
    // Polling lỗi tạm thời (mạng chập chờn) — không chặn ca, thử lại vòng sau.
  }
}

function startDispatchPolling() {
  if (acdPollTimer) return;
  acdLastQueueSids = new Set();
  pollDispatchState();
  // Fallback nhận offer khi không có cable — offer chỉ sống 20s nên polling
  // cũng phải miễn nhiễm throttle tab nền như presence.
  acdPollTimer = startBackgroundSafeInterval(
    pollDispatchState,
    ACD_POLL_INTERVAL_MS
  );
}

function stopDispatchPolling() {
  if (acdPollTimer) acdPollTimer(); // stop function từ startBackgroundSafeInterval
  acdPollTimer = null;
}

// ===== Entry point ACD: khôi phục ca cũ sau reload (T034.a) =====

function startAcdShiftUi(store) {
  acdStoreRef = store;
  renderShiftButton();

  const saved = loadShiftState();
  if (saved) {
    // Reload giữa ca — khôi phục lại (server có thể đã hết TTL presence, nhưng
    // shift/start là idempotent nên gọi lại an toàn, không tạo endpoint trùng).
    startShift(saved.skills);
  }
}

// Gọi 1 lần khi dashboard mount (patch trong useCallSession onMounted).
export const startVoiceBackgroundRegister = store => {
  if (started) return;
  started = true;
  storeRef = store;
  tryStart();
};

// AUTO-START (fix 20/07): useCallSession chỉ mount khi FloatingCallWidget hiện
// (đang có cuộc gọi) → lúc idle không ai gọi startVoiceBackgroundRegister → agent
// không bao giờ register 1001 dù đã đăng nhập. Tự khởi động khi bundle nạp:
// poll Vue app root lấy Vuex store (không đụng file core, idempotent nhờ guard
// `started` — useCallSession có gọi lại cũng không sao).
//
// CHỈ chạy ở chế độ 'legacy' (mặc định). Chế độ 'acd' KHÔNG auto-register 24/7
// — agent chủ động "Vào ca" qua nút nổi (xem khối ACD cuối file).
(function autoStartWhenDashboardReady() {
  if (typeof window === 'undefined') return;
  if (isAcdMode()) return;
  const tick = () => {
    if (started) return;
    const store = document.querySelector('#app')?.__vue_app__?.config
      ?.globalProperties?.$store;
    // Chỉ start trong dashboard đã đăng nhập (route /app/accounts/...) — trang
    // login/public chưa có store hoặc chưa có quyền gọi API.
    if (store && window.location.pathname.includes('/app/accounts/')) {
      startVoiceBackgroundRegister(store);
      return;
    }
    setTimeout(tick, 3000);
  };
  setTimeout(tick, 3000);
})();

// ===== GĐ2 — Queue ACD (T034): nút Vào ca/Ra ca + nhận cuộc qua dispatch =====
// Toàn bộ khối dưới đây CHỈ chạy khi isAcdMode() === true. Nhánh 'legacy' phía
// trên không đổi hành vi — regression guard cho luồng auto-register cũ.
(function autoStartAcdWhenDashboardReady() {
  if (typeof window === 'undefined') return;
  if (!isAcdMode()) return;
  let acdStarted = false;
  const tick = () => {
    if (acdStarted) return;
    const store = document.querySelector('#app')?.__vue_app__?.config
      ?.globalProperties?.$store;
    if (store && window.location.pathname.includes('/app/accounts/')) {
      acdStarted = true;
      startAcdShiftUi(store);
      startVoiceAdminGroups(store); // nút "Nhóm tổng đài" (chỉ admin)
      startVoiceIntegrationConfig(store); // panel cấu hình tổng đài trên trang TinoFone (chỉ admin)
      return;
    }
    setTimeout(tick, 3000);
  };
  setTimeout(tick, 3000);
})();

export default startVoiceBackgroundRegister;
